import { Card } from "@/shared/components/ui/Card"
import { Badge } from "@/shared/components/ui/Badge"
import { formatDate } from "@/shared/lib/utils"
import { Pencil, Trash2, Key, MoreVertical } from "lucide-react"
import { useState, useRef, useEffect } from "react"
import type { FarmUserResponse } from "@/shared/types/api"

interface TeamMemberCardProps {
  member: FarmUserResponse
  onEdit: (member: FarmUserResponse) => void
  onDelete: (member: FarmUserResponse) => void
  onResetPassword: (member: FarmUserResponse) => void
}

export function TeamMemberCard({ member, onEdit, onDelete, onResetPassword }: TeamMemberCardProps) {
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "active":
        return "success"
      case "inactive":
        return "default"
      case "suspended":
        return "danger"
      default:
        return "default"
    }
  }

  const getRoleBadgeVariant = (role: string) => {
    return role === "admin" ? "primary" : "secondary"
  }

  const getInitials = () => {
    if (member.displayName) {
      return member.displayName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    }
    return member.username.slice(0, 2).toUpperCase()
  }

  return (
    <Card className="relative">
      {/* Actions Menu */}
      <div className="absolute top-3 right-3" ref={menuRef}>
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        >
          <MoreVertical className="h-4 w-4" />
        </button>

        {showMenu && (
          <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200/50 py-1 z-10">
            <button
              onClick={() => {
                setShowMenu(false)
                onEdit(member)
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Pencil className="h-4 w-4" />
              Edit Details
            </button>
            <button
              onClick={() => {
                setShowMenu(false)
                onResetPassword(member)
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Key className="h-4 w-4" />
              Reset Password
            </button>
            <hr className="my-1 border-slate-200/50" />
            <button
              onClick={() => {
                setShowMenu(false)
                onDelete(member)
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              Remove
            </button>
          </div>
        )}
      </div>

      {/* Member Info */}
      <div className="flex items-start gap-4 pr-8">
        {/* Avatar */}
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-white font-semibold text-sm">
          {getInitials()}
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <h3 className="font-roboto font-semibold text-gray-900 truncate">
            {member.displayName || member.username}
          </h3>
          <p className="font-roboto text-sm text-gray-500 truncate">@{member.username}</p>

          {/* Badges */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            <Badge variant={getRoleBadgeVariant(member.role)}>{member.role}</Badge>
            <Badge variant={getStatusBadgeVariant(member.status)}>{member.status}</Badge>
          </div>

          {/* Contact Info */}
          {(member.email || member.mobileNumber) && (
            <div className="mt-3 space-y-1">
              {member.email && (
                <p className="font-roboto text-xs text-gray-500 truncate">{member.email}</p>
              )}
              {member.mobileNumber && (
                <p className="font-roboto text-xs text-gray-500">{member.mobileNumber}</p>
              )}
            </div>
          )}

          {/* Last Login & Created */}
          <div className="mt-3 pt-3 border-t border-slate-200/50">
            {member.lastLoginAt ? (
              <p className="font-roboto text-xs text-gray-400">
                Last login: {formatDate(member.lastLoginAt)}
              </p>
            ) : (
              <p className="font-roboto text-xs text-gray-400 italic">Never logged in</p>
            )}
            <p className="font-roboto text-xs text-gray-400 mt-0.5">
              Added {formatDate(member.createdAt)}
            </p>
          </div>
        </div>
      </div>
    </Card>
  )
}
